import axios, { AxiosError } from 'axios';
import { HttpStatusCode, NotificationType, Methods } from '../config';
import addNotification from './addNotification';

const apiUrl = process.env.REACT_APP_API_URL;

const refreshTokenBlacklist = ['auth/login', 'auth/logout', 'auth/refresh'];

let refreshPromise = null;

const shouldCallRefreshToken = (url) => {
  return url ? !refreshTokenBlacklist.map((path) => url.includes(path)).includes(true) : false;
};

const axiosApiInstance = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
});

axiosApiInstance.interceptors.request.use(
  async (config) => {
    let token = getToken();

    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
      config.headers['Content-Type'] = config.formData ? 'multipart/form-data' : 'application/json';
    }

    if (!token && shouldCallRefreshToken(config.url)) {
      if (!refreshPromise) {
        refreshPromise = refreshAuthToken();
      }

      token = await refreshPromise;
      refreshPromise = null;

      if (token) {
        saveToken(token);
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosApiInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (
      error.code === 'ERR_CANCELED' ||
      error.name === 'CanceledError' ||
      error.name === 'AbortError' ||
      error.message === 'canceled'
    ) {
      return Promise.reject('Request canceled');
    }

    if (!error.response && error.message) {
      addNotification({
        content: error.message,
        type: NotificationType.ERROR,
      });
    }

    if (error.response && error.config) {
      const { status, data } = error.response;
      const callRefreshToken = shouldCallRefreshToken(error.config.url);

      if (status !== HttpStatusCode.Unauthorized || (status === HttpStatusCode.Unauthorized && !callRefreshToken)) {
        if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
          const reader = new FileReader();
          reader.readAsText(error.response.data);
          reader.onload = () => {
            const jsonData = JSON.parse(reader.result);
            addNotification({
              content: jsonData.error.message,
              type: NotificationType.ERROR,
            });
          };
        }

        if (data?.error?.message) {
          addNotification({
            content: data.error.message,
            type: NotificationType.ERROR,
          });
        }
      }

      if (status === HttpStatusCode.Unauthorized && callRefreshToken) {
        try {
          if (!refreshPromise) {
            refreshPromise = refreshAuthToken();
          }

          const token = await refreshPromise;
          refreshPromise = null;

          if (token) {
            saveToken(token);
            error.config.headers['Authorization'] = `Bearer ${token}`;
            return axiosApiInstance.request(error.config);
          }
        } catch (e) {
          refreshPromise = null;
        }
      }
    }

    return Promise.reject(error);
  }
);

export const isAxiosError = (e) => {
  return Boolean(e?.isAxiosError);
};

const request = async ({
  resource,
  method = Methods.GET,
  transformResponse,
  headers,
  data,
  formData,
  responseType,
  signal,
  ...requestConfig
}) => {
  const url = `/${resource}`;

  const { data: response, headers: respHeader } = await axiosApiInstance({
    method,
    url,
    headers,
    data,
    formData,
    responseType,
    signal,
    ...requestConfig,
  });
  if (responseType === 'blob') {
    const contentDisposition = respHeader['content-disposition'];
    const fln = contentDisposition?.match(/filename="([^"]+)"/);
    if (fln) response.filename = decodeURIComponent(fln[1]);
    return response;
  }

  if (response.token) {
    saveToken(response.token);
    return { ...response.data, token: response.token };
  }
  return response.data;
};

export default request;

export const saveToken = (token) => {
  return localStorage.setItem('token', token);
};

const getToken = () => {
  return localStorage.getItem('token');
};

const refreshAuthToken = async () => {
  try {
    const response = await axiosApiInstance.get('/auth/refresh');
    return response.data.token;
  } catch (error) {
    console.error('Failed to refresh token', error);
    window.location.href = '/';
    throw error;
  }
};
